import React from 'react';
import { motion } from 'motion/react';
import { Link } from 'react-router-dom';
import { 
  Copyright, 
  Mail, 
  ShieldAlert, 
  FileCheck, 
  AlertTriangle,
  ChevronRight,
  ArrowLeft,
  Scale,
  Signature
} from 'lucide-react';

const CopyrightPolicy: React.FC = () => {
  return (
    <div className="min-h-screen pt-24 pb-20 bg-white relative overflow-hidden font-sans">
      <div className="absolute top-0 right-0 w-full h-[600px] bg-[radial-gradient(circle_at_70%_0%,rgba(244,63,94,0.05)_0%,transparent_70%)] -z-10" />
      
      <div className="max-w-5xl mx-auto px-6 relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease: "easeOut" }}
          className="flex flex-col items-start gap-4 mb-12"
        >
          <Link to="/" className="inline-flex items-center gap-2 text-[10px] font-bold text-zinc-400 hover:text-rose-600 transition-all group uppercase tracking-[0.2em]">
            <ArrowLeft className="w-3 h-3 group-hover:-translate-x-1 transition-transform" />
            Home
          </Link>
          
          <div className="space-y-2">
            <h1 className="text-3xl md:text-5xl font-black font-display text-rose-950 tracking-tighter uppercase">
              Copyright & <span className="italic text-rose-600">DMCA</span>
            </h1>
            <p className="text-zinc-500 font-medium text-sm leading-relaxed max-w-xl">
              We respect intellectual property. This policy details how we handle copyright claims and takedown notices.
            </p>
          </div>

          <div className="flex items-center gap-4 text-[9px] font-bold uppercase tracking-[0.3em] text-rose-400 pt-2 border-t border-rose-600/10 w-full">
            <span>Effective: May 19, 2026</span>
            <span className="w-1 h-1 rounded-full bg-rose-200" />
            <span>Agent ID: FCG-DMCA-01</span>
          </div>
        </motion.div>

        <div className="grid grid-cols-1 gap-8">
          <motion.section 
            initial={{ opacity: 0, scale: 0.98 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            className="bg-white p-8 md:p-10 rounded-[2rem] border border-rose-100 shadow-xl shadow-rose-900/5 hover:shadow-rose-900/10 transition-all group"
          >
            <div className="flex flex-col md:flex-row gap-8 items-start">
              <div className="w-14 h-14 bg-rose-600 rounded-2xl flex items-center justify-center shrink-0 shadow-lg shadow-rose-600/20">
                <ShieldAlert className="w-7 h-7 text-white" />
              </div>
              <div className="space-y-4">
                <h2 className="text-xl font-heavy font-display text-rose-950 uppercase tracking-tight flex items-center gap-3">
                  1. DMCA Notice Procedure
                </h2>
                <div className="space-y-4 text-zinc-600 font-medium leading-relaxed text-sm">
                  <p>
                    Fun Circle Games complies with the Digital Millennium Copyright Act ("DMCA"). If you believe that your work has been copied in a way that constitutes copyright infringement, please provide our Copyright Agent with a written notice containing:
                  </p>
                  <ul className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-4">
                    {[
                      'Physical or electronic signature of authorized person.',
                      'Description of the copyrighted work claimed to be infringed.',
                      'Description of where the material is located on our Site.',
                      'Your address, telephone number, and email address.',
                      'A statement that you have a good faith belief of misuse.',
                      'A statement that the information is accurate under penalty of perjury.'
                    ].map((item, i) => (
                      <li key={i} className="flex gap-4 p-4 bg-rose-50/50 rounded-2xl border border-rose-100">
                        <div className="w-1.5 h-1.5 rounded-full bg-rose-400 mt-1.5 shrink-0" />
                        <span className="text-xs font-bold text-rose-950 leading-tight">{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          </motion.section>

          <motion.section 
            initial={{ opacity: 0, scale: 0.98 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            className="bg-rose-50/20 p-8 md:p-10 rounded-[2rem] border border-rose-100 shadow-xl shadow-rose-900/5"
          >
            <div className="flex flex-col md:flex-row gap-8 items-start text-zinc-600">
              <div className="w-14 h-14 bg-rose-600 rounded-2xl flex items-center justify-center shrink-0 shadow-lg shadow-rose-600/20">
                <Signature className="w-7 h-7 text-white" />
              </div>
              <div className="space-y-4 w-full">
                <h2 className="text-xl font-heavy font-display text-rose-950 uppercase tracking-tight italic">Designated Copyright Agent</h2>
                <p className="text-sm">Please send all notices to the following designated agent:</p>
                <div className="p-6 bg-white rounded-2xl border border-rose-100 space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <span className="block text-[10px] font-black opacity-50 uppercase mb-1">Email Notice</span>
                      <span className="text-rose-600 font-bold text-lg hover:underline underline-offset-4">dmca@funcirclegames.com</span>
                    </div>
                    <div>
                      <span className="block text-[10px] font-black opacity-50 uppercase mb-1">Attention</span>
                      <span className="text-rose-950 font-bold text-lg tracking-tight">Copyright Compliance Dept.</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </motion.section>

          <motion.section 
            initial={{ opacity: 0, scale: 0.98 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            className="bg-white p-8 md:p-10 rounded-[2rem] border border-rose-100 shadow-xl shadow-rose-900/5 group"
          >
            <div className="flex flex-col md:flex-row gap-8 items-start">
              <div className="w-14 h-14 bg-rose-600 rounded-2xl flex items-center justify-center shrink-0">
                <AlertTriangle className="w-7 h-7 text-white" />
              </div>
              <div className="space-y-6">
                <h2 className="text-xl font-heavy font-display text-rose-950 uppercase tracking-tight">2. Repeat Infringer Policy</h2>
                <div className="space-y-4 text-zinc-600 font-medium leading-relaxed text-sm">
                  <p>
                    Fun Circle Games maintains a zero-tolerance policy for repeat copyright infringement. We reserve the right to <strong className="text-rose-700 underline decoration-rose-200 underline-offset-4">permanently disable or terminate accounts</strong> found to have repeatedly uploaded copyrighted music, MIDI files, or other assets without proper license or authorization.
                  </p>
                  <p className="p-4 bg-rose-50 rounded-xl border border-rose-100 text-rose-900 text-xs font-bold leading-relaxed">
                    Note: Since our platform serves as a private creator workspace, takedown notices typically result in the removal of the specific asset from your private library and the disabling of any renders/exports associated with said asset.
                  </p>
                </div>
              </div>
            </div>
          </motion.section>

          <motion.section 
            initial={{ opacity: 0, scale: 0.98 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            className="bg-rose-50/20 p-8 md:p-10 rounded-[2rem] border border-rose-100"
          >
            <div className="flex flex-col md:flex-row gap-8 items-start">
              <div className="w-14 h-14 bg-rose-600 rounded-2xl flex items-center justify-center shrink-0">
                <FileCheck className="w-7 h-7 text-white" />
              </div>
              <div className="space-y-6">
                <h2 className="text-xl font-heavy font-display text-rose-950 uppercase tracking-tight italic">3. Counter-Notification</h2>
                <div className="space-y-4 text-zinc-600 font-medium leading-relaxed text-sm font-sans">
                  <p>
                    If you believe your content was removed by mistake or misidentification, you may submit a counter-notice to our agent. This counter-notice must include your contact information, identification of the removed material, and a statement (under penalty of perjury) of your good-faith belief that the removal was in error.
                  </p>
                </div>
              </div>
            </div>
          </motion.section>

          <motion.section 
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="pt-8"
          >
            <div className="bg-white p-10 md:p-14 rounded-[3rem] border border-rose-100 shadow-2xl shadow-rose-950/5 relative overflow-hidden font-sans">
              <div className="absolute top-0 right-0 w-96 h-96 bg-rose-500/5 rounded-full blur-[100px] -z-0" />
              <div className="relative z-10 grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
                <div className="space-y-4">
                  <h2 className="text-2xl md:text-3xl font-heavy font-display text-rose-950 uppercase tracking-tight leading-none italic">
                    Copyright <br />
                    <span className="text-rose-600/60">Compliance</span>
                  </h2>
                  <p className="text-zinc-500 font-medium text-xs leading-relaxed">
                    By using Fun Circle Games, you acknowledge that you are responsible for the legality of all uploaded media.
                  </p>
                </div>
                <div className="bg-rose-50 p-6 rounded-3xl border border-rose-100">
                   <p className="text-[10px] font-black uppercase text-rose-600 tracking-[0.2em] mb-2 font-display">Legal Disclaimer</p>
                   <p className="text-[10px] font-bold text-rose-900 leading-relaxed uppercase tracking-wider italic">
                     MISREPRESENTATIONS IN A TAKEDOWN NOTICE MAY RESULT IN LIABILITY FOR DAMAGES, INCLUDING COSTS AND ATTORNEYS' FEES.
                   </p>
                </div>
              </div>
            </div>
          </motion.section>
        </div>
      </div>
    </div>
  );
};

export default CopyrightPolicy;
